import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { HttpResponse } from '@angular/common/http';
import { catchError, map, Observable, of, tap, throwError } from 'rxjs';
import { User, UserCategory, UserType } from '../models/user';
import { LogoutResponse } from '../models/generic';
import { ValidateOtpResponse } from '../models/validate-otp';
import { BaseUserService } from '../../base/services/base-user.service';
import { UserInfoResponse } from '../models/user-info-response';
import { OnboardingResponse } from '../models/onboarding-response';
import {
  ACTIVE_SESSION,
  IAM_PROFILE_KEY,
  LEGACY_USER_ID_KEY,
  USER_KEY,
} from './storage.service';

export interface OnboardingRequest {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  dateOfBirth: string | null | undefined;
}

export interface EmailVerifyOtpResponse {
  otpVerified: boolean;
  secondaryIdentifierRequired: boolean;
  onboardingRequired: boolean;
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  accessTokenExpiresIn: number;
  refreshExpiresIn: number;
}

export interface MobileVerifyOtpResponse {
  otpVerified: boolean;
  secondaryIdentifierRequired: boolean;
  onboardingRequired: boolean;
}

export interface RefreshTokenResponse {
  message?: string;
  accessToken: string;
  refreshToken?: string;
  tokenType?: string;
  accessTokenExpiresIn?: number;
  refreshExpiresIn?: number;
  accessTokenIssuedAt?: number;
  onboardingRequired?: boolean;
}

export interface IamUserProfileResponse {
  identity?: {
    maskedUserId?: string;
    installerId?: string;
    email?: string;
    phone?: string;
    countryCode?: string;
    countryIso?: string;
    emailVerified?: boolean;
    phoneVerified?: boolean;
  };
  account?: {
    status?: string;
    onboardingCompleted?: boolean;
    onboardingCompletedAt?: number;
    pendingDeletion?: boolean;
  };
  profile?: {
    firstName?: string;
    lastName?: string;
    fullName?: string;
    dateOfBirth?: number;
    company?: string;
    jobTitle?: string;
    locale?: string;
  };
  location?: {
    countryCode?: string;
    fullAddress?: string;
  };
  preferences?: {
    preferences?: string;
    notificationPreferences?: string;
  };
  consent?: {
    marketingConsent?: boolean;
  };
  meta?: {
    createdAt?: number;
    updatedAt?: number;
    profileComplete?: boolean;
    completionPercentage?: number;
  };
}

interface AccessTokenClaims {
  sub?: string;
  sess_id?: string;
  tenant_id?: string;
  key_ver?: string;
  iss?: string;
  aud?: string;
  iat?: number;
  exp?: number;
  primary_role?: string;
  secondary_roles?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseUserService {
  private roles: string[] = [];
  private primaryRole: string | null = null;
  private secondaryRoles: string[] = [];

  authenticatedUser = new BehaviorSubject<User | null>(null);
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshExpiryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly refreshBeforeExpiryMs = 5 * 60 * 1000;
  private readonly maxTimeoutMs = 2_147_483_647;
  private accessToken: string | null = null;
  private accessTokenExpiresAt: number | null = null;
  private refreshTokenExpiresAt: number | null = null;
  loggedUser: User = this.storageService.getObject(USER_KEY);

setRoles(roles: string[] | undefined): void {
  const normalizedRoles = [
    ...new Set(
      (roles ?? []).filter(
        (role): role is string => Boolean(role)
      )
    ),
  ];
 
  this.roles = normalizedRoles;
 
  this.primaryRole =
    normalizedRoles[0] ?? null;
 
  this.secondaryRoles =
    normalizedRoles.filter(
      role => role !== this.primaryRole
    );
}
 

  getRoles(): (string | undefined)[] {
    return this.roles;
  }

  setPrimaryRole(primaryRole?: string): void {
    this.primaryRole = primaryRole || null;
    if (this.primaryRole && this.roles.length === 0) {
      this.roles = [this.primaryRole];
    }
    if (this.primaryRole && !this.roles.includes(this.primaryRole)) {
      this.roles = [this.primaryRole, ...this.roles];
    }
    this.secondaryRoles = this.roles.filter(
      (role, index, roleList) => role !== this.primaryRole && roleList.indexOf(role) === index,
    );
  }

  getPrimaryRole(): string | null {
    return this.primaryRole;
  }

  setSecondaryRoles(secondaryRoles: string[] | undefined): void {
    this.secondaryRoles = (secondaryRoles ?? []).filter(
      (role): role is string => Boolean(role),
    );

    const primaryRole = this.primaryRole ?? this.roles[0] ?? null;
    this.roles = [
      ...(primaryRole ? [primaryRole] : []),
      ...this.secondaryRoles.filter((role) => role !== primaryRole),
    ];
  }

  getSecondaryRoles(): string[] {
    return this.secondaryRoles;
  }

  setAccessToken(token: string): void {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    if (this.accessToken) {
      return this.accessToken;
    }
    return this.accessToken;
  }

  clearAccessToken(): void {
    this.accessToken = null;
  }

  constructor() {
    super();
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.refreshExpiryTimer !== null) {
      clearTimeout(this.refreshExpiryTimer);
      this.refreshExpiryTimer = null;
    }
  }

  private clearAuthState(): void {
    this.clearRefreshTimer();
    this.clearAccessToken();
    this.accessTokenExpiresAt = null;
    this.refreshTokenExpiresAt = null;
    this.storageService.remove(IAM_PROFILE_KEY);
    this.storageService.remove(LEGACY_USER_ID_KEY);
    this.storageService.remove(USER_KEY);
    this.storageService.remove(
      ACTIVE_SESSION
    );
    this.loggedUser = {} as User;
    this.authenticatedUser.next(null);

    this.setRoles([]);
    this.setPrimaryRole();
    this.setSecondaryRoles([]);
  }

  private redirectToLogin() {
    this.router.navigate(['/login'], {
      queryParams: { type: 'customer' },
      replaceUrl: true,
    });
  }

  private handleRefreshTokenExpiry() {
    this.clearAuthState();
    this.redirectToLogin();
  }

  private calculateExpiresAt(expiresInSeconds?: number): number | null {
    if (
      expiresInSeconds === undefined ||
      expiresInSeconds === null ||
      !Number.isFinite(expiresInSeconds) ||
      expiresInSeconds <= 0
    ) {
      return null;
    }

    return Date.now() + expiresInSeconds * 1000;
  }

  private updateRolesFromAccessToken(accessToken: string): void {
    const decodedToken = this.decodeAccessToken(accessToken);
    const primaryRole = decodedToken?.primary_role;
    const secondaryRoles = Array.isArray(decodedToken?.secondary_roles)
      ? decodedToken.secondary_roles
      : [];

    const roles = [primaryRole, ...secondaryRoles].filter(
      (role): role is string => Boolean(role),
    );

    this.setRoles(roles);
    this.persistUserFromAccessToken(accessToken);
  }

public storeTokenBundle(
  accessToken: string,
  tokenType: string = 'Bearer',
  accessTokenExpiresIn?: number,
  refreshExpiresIn?: number,
): void {
  this.setAccessToken(accessToken);
 
  this.accessTokenExpiresAt =
    this.calculateExpiresAt(accessTokenExpiresIn);
 
  const newRefreshExpiryAt =
    this.calculateExpiresAt(refreshExpiresIn);
 
  if (newRefreshExpiryAt !== null) {
    this.refreshTokenExpiresAt = newRefreshExpiryAt;
  }
 
  this.updateRolesFromAccessToken(accessToken);
}
public startTokenRefreshCycle(): void {
  this.scheduleAccessTokenRefresh();
  this.scheduleRefreshTokenExpiry();
}
 

  private scheduleAccessTokenRefresh(): void {
    if (!this.accessTokenExpiresAt) {
      return;
    }

    if (this.refreshTokenExpiresAt && Date.now() >= this.refreshTokenExpiresAt) {
      this.handleRefreshTokenExpiry();
      return;
    }

    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
    }

    const refreshAt = this.accessTokenExpiresAt - this.refreshBeforeExpiryMs;
    const remainingMs = Math.max(0, refreshAt - Date.now());
    const delay = Math.min(remainingMs, this.maxTimeoutMs);

    this.refreshTimer = setTimeout(() => {
      this.refreshAccessToken().subscribe({
        error: () => {},
      });
    }, delay);
  }

  private scheduleRefreshTokenExpiry(): void {
    if (!this.refreshTokenExpiresAt) {
      return;
    }

    if (this.refreshExpiryTimer !== null) {
      clearTimeout(this.refreshExpiryTimer);
      this.refreshExpiryTimer = null;
    }

    const remainingMs = Math.max(0, this.refreshTokenExpiresAt - Date.now());
    const delay = Math.min(remainingMs, this.maxTimeoutMs);

    this.refreshExpiryTimer = setTimeout(() => {
      this.handleRefreshTokenExpiry();
    }, delay);
  }
public initializeTokenRefresh(): Observable<boolean> {
  const hasSession =
    this.storageService.get(ACTIVE_SESSION) === 'true';
 
  if (!hasSession) {
    return of(false);
  }
 
  return this.refreshAccessToken().pipe(
    map(() => true),
    catchError(() => of(false)),
  );
}

  private mapRoleNameToUserType(role?: string): UserType {
    const mappedRole = this.tryMapRoleNameToUserType(role);
    return mappedRole ?? UserType.CUSTOMER;
  }

  private tryMapRoleNameToUserType(role?: string): UserType | null {
    const roleMap: Record<string, UserType> = {
      USER_BASIC: UserType.CUSTOMER,
      CUSTOMER: UserType.CUSTOMER,
      ADMIN: UserType.ADMIN,
      SYSTEM_INTEGRATOR: UserType.SYSTEM_INTEGRATOR,
      SECONDARY_USER: UserType.SECONDARY_USER,
      THIRD_PARTY_INTEGRATOR: UserType.THIRD_PARTY_INTEGRATOR,
      LARGE_PLANT_USER: UserType.LARGE_SCALE,
      WEB_DYN: UserType.WEB_DYN,
    };

    if (!role) return null;
    return roleMap[role.toUpperCase()] ?? null;
  }

  private decodeJwtPayload<T extends object>(token?: string): T | null {
    if (!token) return null;

    const tokenParts = token.split('.');
    if (tokenParts.length < 2) return null;

    try {
      const payload = tokenParts[1].replaceAll('-', '+').replaceAll('_', '/');
      const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      const decodedPayload = atob(paddedPayload);
      return JSON.parse(decodedPayload) as T;
    } catch {
      return null;
    }
  }

  public decodeAccessToken<T extends object = AccessTokenClaims>(
    token?: string,
  ): T | null {
    return this.decodeJwtPayload<T>(token);
  }

  private createEmptyUser(): User {
    return {
      id: '',
      phone: '',
      name: '',
      email: '',
      isPlantExists: false,
      androidVersion: '',
      appVersion: '',
      mobileMake: '',
      mobileModel: '',
      isBMSPlantExists: false,
      active: true,
      secondryRoles: [],
      primary_role: '',
      user_type: UserType.CUSTOMER,
      secondary_roles: [],
      installer_id: '',
      user_image: '',
      userCategory: UserCategory.FREE,
    };
  }

  private extractSecondaryRoleTypes(claims: AccessTokenClaims): UserType[] {
    if (!Array.isArray(claims.secondary_roles)) return [];

    return claims.secondary_roles
      .map((role) => this.tryMapRoleNameToUserType(role))
      .filter((role): role is UserType => role !== null)
      .filter((role, index, roleList) => roleList.indexOf(role) === index);
  }

  private persistUserFromAccessToken(accessToken?: string) {
    const claims = this.decodeJwtPayload<AccessTokenClaims>(accessToken);
    if (!claims) return;

    const storedUser = this.storageService.getObject(USER_KEY) as Partial<User>;
    const baseUser: User = {
      ...this.createEmptyUser(),
      ...storedUser,
    };

    const secondaryRoles = this.extractSecondaryRoleTypes(claims);
    const mappedUser: User = {
      ...baseUser,
      id: claims.sub || baseUser.id,
      primary_role: claims.primary_role || baseUser.primary_role || '',
      user_type: this.mapRoleNameToUserType(claims.primary_role),
      secondary_roles: secondaryRoles,
      secondryRoles: secondaryRoles,
    };

    this.storageService.setObject(USER_KEY, mappedUser);
    if (mappedUser.id) {
      this.storageService.set(LEGACY_USER_ID_KEY, mappedUser.id);
    }
    this.loggedUser = mappedUser;
    this.authenticatedUser.next(mappedUser);
  }

  private mapIamProfileToUser(profile: IamUserProfileResponse): User {
    const existingUser = this.storageService.getObject(USER_KEY) as Partial<User>;
    const baseUser: User = {
      ...this.createEmptyUser(),
      ...existingUser,
    };
    const firstName = profile.profile?.firstName?.trim() || '';
    const lastName = profile.profile?.lastName?.trim() || '';
    const fallbackName = [firstName, lastName].filter(Boolean).join(' ').trim();

    return {
      ...baseUser,
      name: profile.profile?.fullName || fallbackName || firstName || baseUser.name,
      firstName: firstName || baseUser.firstName,
      lastName: lastName || baseUser.lastName,
      installer_id: profile.identity?.installerId || baseUser.installer_id,
    };
  }

  private persistAuthenticatedIamProfile(profile: IamUserProfileResponse) {
    const mappedUser = this.mapIamProfileToUser(profile);
    this.storageService.setObject(USER_KEY, mappedUser);
    this.storageService.setObject(IAM_PROFILE_KEY, profile);
    this.loggedUser = mappedUser;
    this.authenticatedUser.next(mappedUser);
  }

  fetchIamUserProfile(): Observable<IamUserProfileResponse> {
    return this.http
      .get<IamUserProfileResponse>(this.env.users.baseUrl + '/api/v1/users/profile', {
        withCredentials: true,
      })
      .pipe(
        tap((profile: IamUserProfileResponse) => {
          this.persistAuthenticatedIamProfile(profile);
        }),
      );
  }

  refreshAccessToken(): Observable<HttpResponse<RefreshTokenResponse>> {
    return this.http
      .post<RefreshTokenResponse>(
        this.env.iam.baseUrl + '/v1/auth/refresh',
        {},
        {
          observe: 'response',
          withCredentials: true,
        },
      )
      .pipe(
        tap((response) => {
          const body = response.body;

          if (!body?.accessToken) {
            this.handleRefreshTokenExpiry();
            return;
          }

          this.setAccessToken(body.accessToken);

          const newAccessExpiryAt = this.calculateExpiresAt(body.accessTokenExpiresIn);
          if (newAccessExpiryAt !== null) {
            this.accessTokenExpiresAt = newAccessExpiryAt;
          }

          const newRefreshExpiryAt = this.calculateExpiresAt(body.refreshExpiresIn);
          if (newRefreshExpiryAt !== null) {
            this.refreshTokenExpiresAt = newRefreshExpiryAt;
          }

          this.updateRolesFromAccessToken(body.accessToken);
          this.scheduleAccessTokenRefresh();
          this.scheduleRefreshTokenExpiry();
        }),
        catchError((error) => {
          const errorCode = error?.error?.errorCode;

          if (error?.status === 401 || errorCode === 'AUTH_017') {
            this.handleRefreshTokenExpiry();
          }

          return throwError(() => error);
        }),
      );
  }

  logout(logoutAllDevices = false): Observable<LogoutResponse> {
    return this.http
      .post<LogoutResponse>(
        this.env.iam.baseUrl + '/v1/auth/logout',
        {},
        {
          params: {
            logoutAllDevices: String(logoutAllDevices),
          },
        },
      )
      .pipe(
        tap(() => {
          this.clearAuthState();
        }),
      );
  }

  login(
    identifier: string,
    identifierType: 'email' | 'mobile',
    countryCode?: string | null,
  ): Observable<HttpResponse<User>> {
    let url: string;
    let body: any;

    if (identifierType === 'email') {
      url = this.env.iam.baseUrl + '/v1/auth/login';
      body = {
        identifier,
        identifierType: 'email',
      };
    } else {
      url = this.env.iam.baseUrl + '/v1/auth/otp/send';
      body = {
        secondaryType: 'mobile',
        countryIso: 'IN',
        countryCode,
        identifier,
      };
    }

    return this.http.post<User>(url, body, { observe: 'response' });
  }

  verifyLoginOtp(
    identifier: string,
    otp: string,
    identifierType: 'email' | 'mobile',
    countryCode?: string | null,
  ): Observable<HttpResponse<EmailVerifyOtpResponse>> {
    const body: any = {
      otp: Number(otp),
      identifier,
      identifierType,
    };

    if (identifierType === 'mobile') {
      body.countryCode = countryCode;
      body.countryIso = 'IN';
    }

    return this.http
      .post<EmailVerifyOtpResponse>(this.env.iam.baseUrl + '/v1/auth/otp/verify', body, {
        observe: 'response',
        withCredentials: true,
      })
      .pipe(
        tap((response) => {
          const body = response.body;

          if (!body?.accessToken) {
            return;
          }

          this.storeTokenBundle(
            body.accessToken,
            body.tokenType ?? 'Bearer',
            body.accessTokenExpiresIn,
            body.refreshExpiresIn,
          );
        }),
      );
  }

  completeOnboarding(onboardingRequest: OnboardingRequest) {
    const requestBody: OnboardingRequest = {
      firstName: onboardingRequest.firstName,
      lastName: onboardingRequest.lastName,
      dateOfBirth: onboardingRequest.dateOfBirth,
    };

    return this.http.post<OnboardingResponse>(
      this.env.users.baseUrl + '/api/v1/users/onboarding',
      requestBody,
    );
  }

  validateOtp(
    email: string,
    code: string,
  ): Observable<HttpResponse<ValidateOtpResponse>> {
    return this.http.post<ValidateOtpResponse>(
      this.env.externalUrls.user.baseUrl + 'users/otp/validateNew',
      { email, code },
      {
        observe: 'response',
      },
    );
  }

  silogin(mobile: string, password: string) {
    return this.http.post(
      this.env.externalUrls.system_integration.loginUrl,
      { phone: mobile, password },
      { observe: 'response' },
    );
  }

  fetchUserInfo(userId: string): Observable<UserInfoResponse> {
    return this.http.get<UserInfoResponse>(
      this.env.externalUrls.user.baseUrl + 'users/userInfo/' + userId,
    );
  }

  get user(): User | null {
    return this.authenticatedUser.value;
  }

  set user(user: User | null) {
    this.authenticatedUser.next(user);
  }
  
 loginHolium(username:string,password:string){
  let url=this.env.externalUrls.holium.loginUrl
  let loginParams={"params":{"username":username,"password":password}}
  return this.http.post(url,loginParams)
 }

 
}